import { Box, Dialog, styled, Typography, useTheme } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import { Button } from 'components'
import { useAccessRequestPolicies, useChangeAccessRequestPolicy } from 'hooks'
import { Icon } from 'packages/eid-icons'
import { Select, Tooltip } from 'packages/eid-ui'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

const useStyles = makeStyles((theme) => ({
    AddAccessModalMain: {
        '& .MuiDialog-paper': {
            maxWidth: '440px',
        },
    },
    NewAccessAssignmentModal: {
        '& h2': {
            fontSize: '24px',
            color: '#1b1f1f',
            fontWeight: 'normal',
        },
    },

    AccessRequestModalHeader: {
        padding: '32px 40px',
        '& svg': {
            top: '-14px !important',
        },
    },

    AccessRequestModalFooter: {
        display: 'flex',
        padding: '32px 40px',
        gap: '16px',

        '& button': {
            border: `solid 1px ${theme?.palette?.primary?.main}`,
            padding: '8px 12px',
            height: '32px',
            fontSize: '14px',
            '&:nth-child(1)': {
                background: theme?.palette?.primary?.main,
                color: '#fff',
            },
            '&:nth-child(2)': {
                color: theme?.palette?.primary?.main,
                maxWidth: '90px',
            },
        },
    },

    newDirectAssigned: {
        padding: ' 0 40px 32px',
        borderBottom: '1px solid #d8d8d8 !important',
    },
    searchInput: {
        ' & input': {
            height: '40px',
            marginTop: '12px',
            fontSize: '16px',
            '&::placeholder': {
                color: '#b4b4b4',
            },
        },
    },
}))

const CustomDialog = styled(Dialog)({
    '& .MuiDialog-paperWidthSm': {
        maxWidth: '560px',
        width: '100%',
        background: '#f7f8fa',
        '& .MuiBox-root': {
            maxWidth: '830px',
            width: '100%',
        },
    },
    '& .MuiBox-root': {
        border: 'none',
        background: ' #f7f8fa',
        position: 'relative',
    },
})

const CloseIconeBox = styled(Box)({
    '& svg': {
        position: 'absolute',
        top: '-20px',
        right: '-20px',
        cursor: 'pointer',
    },
})

const ChangeAccessRequestPolicy = ({ resourceId, refetch }) => {
    const { t } = useTranslation()
    const theme = useTheme()
    const classes = useStyles({})
    const [selectedPolicy, setSelectecPolicy] = useState(null)
    const [requestPolicyList, setRequestPolicyList] = useState([])
    const [openModal, setOpenModal] = useState(false)
    const { isLoading, data } = useAccessRequestPolicies()

    const [changeRequestPolicy, { isLoading: isChangeLoading }] =
        useChangeAccessRequestPolicy(resourceId)

    useEffect(() => {
        if (Object.keys(data).length > 0) {
            const policyDropdownOptions = Object.keys(data).map((policyId) => {
                return {
                    id: policyId,
                    value: data[policyId].friendlyName,
                    isSelectableInUi: data[policyId].isSelectableInUi,
                }
            })
            setRequestPolicyList(
                policyDropdownOptions?.length
                    ? policyDropdownOptions.filter((x) => x.isSelectableInUi)
                    : [],
            )
        }
    }, [data])

    const handlePolicyChange = (e) => {
        setSelectecPolicy(e)
    }

    const getOptionLabel = (option) => option.value

    return (
        <Box>
            <Button
                onClick={() => setOpenModal(true)}
                style={{
                    borderRadius: '4px',
                    padding: '3px 13px',
                    backgroundColor: theme?.palette?.primary?.main,
                    color: '#ffffff',
                    fontSize: '14px',
                    height: '32px',
                    '&:hover': {
                        backgroundColor: theme?.palette?.primary?.main,
                    },
                }}
            >
                {t('ChangePolicy')}
            </Button>
            <CustomDialog
                open={openModal}
                onClose={() => setOpenModal(false)}
                title={t('')}
                className={classes.AddAccessModalMain}
            >
                <Box className={classes.NewAccessAssignmentModal}>
                    <Box className={classes.AccessRequestModalHeader}>
                        <CloseIconeBox onClick={() => setOpenModal(false)}>
                            <Icon
                                name="Close"
                                width="16px"
                                height="16px"
                                color="#959598"
                            />
                        </CloseIconeBox>
                        <Typography variant="h2">
                            {t('ChangePolicy')}
                        </Typography>
                    </Box>
                    <Box className={classes.newDirectAssigned}>
                        <Typography variant="subtitle1">
                            {t('SelectAccessRequestPolicy')}
                        </Typography>
                        <Select
                            options={requestPolicyList}
                            getOptionLabel={getOptionLabel}
                            onChange={(e) => handlePolicyChange(e)}
                            value={selectedPolicy}
                            isLoading={isLoading}
                            placeholder={t('SelectAccessRequestPolicy')}
                        />
                    </Box>
                    <Box className={classes.AccessRequestModalFooter}>
                        <Tooltip
                            title={
                                !selectedPolicy
                                    ? t('SelectAccessRequestPolicy')
                                    : ''
                            }
                        >
                            <Box style={{ width: 'auto' }}>
                                <Button
                                    disabled={
                                        isChangeLoading || !selectedPolicy
                                    }
                                    loading={isChangeLoading}
                                    onClick={() =>
                                        changeRequestPolicy(
                                            selectedPolicy.id,
                                        ).then((data) => {
                                            refetch()
                                            setOpenModal(false)
                                            setSelectecPolicy(null)
                                        })
                                    }
                                    backgroundColor={
                                        theme?.palette?.primary?.main
                                    }
                                    disabledStyles={{
                                        backgroundColor: '#aab0b4 !important',
                                        borderColor: '#aab0b4 !important',
                                        color: '#ffffff !important',
                                    }}
                                >
                                    {t('ChangePolicy')}
                                </Button>
                            </Box>
                        </Tooltip>
                        <Button
                            onClick={() => {
                                setOpenModal(false)
                                setSelectecPolicy(null)
                            }}
                        >
                            {t('Cancel')}
                        </Button>
                    </Box>
                </Box>
            </CustomDialog>
        </Box>
    )
}

export default ChangeAccessRequestPolicy
